import { useCallback, useEffect, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { defaultContentLanguageFromUi } from '@/i18n'
import { knowledgeExamplePromptsApi, KnowledgeExamplePrompt } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { cn } from '@/lib/utils'

const NO_PROMPTS: Array<KnowledgeExamplePrompt> = []

interface ExamplePromptChipsProps {
  onSelect: (prompt: KnowledgeExamplePrompt) => void
  className?: string
  /** Prefix for kebab-case data-testids (`example-prompt-chip-0`). */
  testIdPrefix?: string
  /** Output / content language for composed sentences (same rail as the greeting). */
  language?: string
}

/**
 * Lobby example-prompt starter (R3.6 / polish B1).
 * One visible suggestion; refresh cycles the cached org pool (no extra LLM call).
 * The parent stamps `sourcePromptId` on thread create.
 */
export function ExamplePromptChips(props: ExamplePromptChipsProps) {
  const {
    onSelect,
    className,
    testIdPrefix = 'example-prompt-chip',
    language,
  } = props

  const { t, i18n } = useTranslation('app')
  const { user } = useAuth()
  const organizationToken = user?.organizationToken
  const [visibleIndex, setVisibleIndex] = useState(0)
  const resolvedLanguage = language ?? defaultContentLanguageFromUi(i18n.language)

  // The pool is per org and language (the server caches it per org), so the key
  // carries both: a remount or a second caller reuses it, and a re-login into
  // another org never shows the previous org's prompts. The chip is decorative,
  // so a failed fetch renders nothing and is not retried.
  const { data } = useSWR(
    organizationToken ? ['example-prompts', organizationToken, resolvedLanguage] : null,
    ([, , lang]) => knowledgeExamplePromptsApi.list(lang),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      dedupingInterval: 60_000,
    },
  )
  const prompts = data ?? NO_PROMPTS

  useEffect(() => {
    setVisibleIndex(0)
  }, [prompts])

  const visiblePrompt = prompts[visibleIndex] ?? prompts[0]
  const canRefresh = prompts.length > 1

  const handleRefresh = useCallback(() => {
    setVisibleIndex((current) => {
      if (prompts.length < 2) {
        return current
      }
      return (current + 1) % prompts.length
    })
  }, [prompts.length])

  const handleSelect = useCallback(() => {
    if (!visiblePrompt) {
      return
    }
    onSelect(visiblePrompt)
  }, [onSelect, visiblePrompt])

  if (!visiblePrompt) {
    return null
  }

  return (
    <div
      className={cn('flex w-full min-w-0 items-start gap-2', className)}
      data-testid="example-prompt-chips"
    >
      <button
        key={visiblePrompt.promptId}
        type="button"
        data-testid={`${testIdPrefix}-0`}
        className="group flex min-w-0 flex-1 items-start gap-2 text-left"
        onClick={handleSelect}
      >
        <Plus
          aria-hidden
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--home-pill-inactive-fg)] transition-colors duration-200 group-hover:text-[var(--home-pill-inactive-fg-hover)]"
        />
        <span className="line-clamp-2 min-w-0 text-[13px] font-normal text-[var(--home-pill-inactive-fg)] transition-colors duration-200 group-hover:text-[var(--home-pill-inactive-fg-hover)]">
          {visiblePrompt.text}
        </span>
      </button>
      {canRefresh ? (
        <button
          type="button"
          data-testid="example-prompt-refresh"
          aria-label={t('homeLab.examplePrompt.refreshAria')}
          title={t('homeLab.examplePrompt.refreshAria')}
          className="mt-0.5 shrink-0 rounded-md p-0.5 text-[var(--home-pill-inactive-fg)] transition-colors duration-200 hover:text-[var(--home-pill-inactive-fg-hover)]"
          onClick={handleRefresh}
        >
          <RefreshCw aria-hidden className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}
